import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { ActionSubjectIdType, SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { Spotlight } from '@trello/nachos/onboarding-experimental';
import { useRecentBoards } from '@trello/recent-boards';
import { useSharedStateSelector } from '@trello/shared-state';
import { useSplitScreenSharedState } from '@trello/split-screen';
import { token } from '@trello/theme';

import { boardSwitcherSharedState } from 'app/src/components/BoardSwitcher/boardSwitcherSharedState';
import { useMemberInboxIds } from 'app/src/components/Inbox/useMemberInboxIds';
import { usePersonalProductivityLocalOverride } from 'app/src/components/PersonalProductivityBeta';
import { useBoardWorkspaceFragment } from './BoardWorkspaceFragment.generated';
import { useNewUserCohortFragment } from './NewUserCohortFragment.generated';
import { newUserShowInboxSpotlightSharedState } from './newUserShowInboxSpotlightSharedState';
import { personalProductivityOnboardingStepSharedState } from './personalProductivityOnboardingStepSharedState';

// eslint-disable-next-line @trello/assets-alongside-implementation
import existingUserBoardSwitcherImage from 'resources/images/discovery-onboarding/existing-user-board-switcher.gif';
// eslint-disable-next-line @trello/assets-alongside-implementation
import newUserInboxSpotlightImage from 'resources/images/discovery-onboarding/inbox-spotlight.gif';
// eslint-disable-next-line @trello/assets-alongside-implementation
import newUserOnboardingListSpotlightImage from 'resources/images/discovery-onboarding/onboarding-list-spotlight.gif';
// eslint-disable-next-line @trello/assets-alongside-implementation
import newUserPanelSpotlightImage from 'resources/images/discovery-onboarding/panels-spotlight.gif';

export type NewUserSpotlightSource = 'inbox' | 'list' | 'panelNavigation';
export type ExistingUserSpotlightSource =
  | 'boardSwitcherModal'
  | 'inbox'
  | 'panelNavigation';

interface NewUserSpotlightTourProps {
  source: ExistingUserSpotlightSource | NewUserSpotlightSource;
  hideSpotlight?: boolean;
  listId?: string;
  boardId: string | null;
  onDismissSpotlight?: () => void;
}

interface AnalyticsAttributes {
  spotlightName: SourceType;
  nextButton: ActionSubjectIdType;
  dismissButton: ActionSubjectIdType;
  componentName: ActionSubjectIdType;
}

// One Time Messages
export const NEW_USER_SPOTLIGHT_TOUR_MESSAGE_ID = 'new-user-spotlight-tour';
const PREMIUM_TRIAL_MODAL_MESSAGE_DISMISSED_KEY_PREFIX =
  'premium-trial-modal-message-dismissed-for-ws';

// Prefix to identify the onboarding list
const NEW_USER_ONBOARDING_LIST_PREFIX = 'getting-started-list';
const NEW_USER_DEFAULT_BOARD_PREFIX = 'default-board';

// Spotlight Targets
export const PANEL_NAVIGATION_SPOTLIGHT = 'panel-navigation-spotlight';
export const INBOX_SPOTLIGHT = 'inbox-spotlight';
export const INBOX_SPOTLIGHT_EXISTING_USER = 'inbox-spotlight-existing-user';
export const ONBOARDING_LIST_SPOTLIGHT = 'onboarding-list-spotlight';
export const BOARD_SWITCHER_SPOTLIGHT = 'board-switcher-spotlight';
export const ISLAND_NAV_SPOTLIGHT = 'island-nav-spotlight-tour';

// Spotlight Targets radii
const PANEL_NAVIGATION_SPOTLIGHT_RADIUS = 12;
const ONBOARDING_LIST_SPOTLIGHT_RADIUS = 12;
const INBOX_SPOTLIGHT_RADIUS = 16;

// Analytics attributes
const analyticsSourcesMap: Record<
  NewUserSpotlightTourProps['source'],
  AnalyticsAttributes
> = {
  panelNavigation: {
    spotlightName: 'islandNavigationSpotlight',
    componentName: 'islandNavigationSpotlight',
    nextButton: 'islandNavigationSpotlightNextButton',
    dismissButton: 'islandNavigationSpotlightDismissButton',
  },
  inbox: {
    spotlightName: 'inboxSpotlight',
    componentName: 'inboxSpotlight',
    nextButton: 'inboxSpotlightNextButton',
    dismissButton: 'inboxSpotlightDismissButton',
  },
  list: {
    spotlightName: 'onboardingListSpotlight',
    componentName: 'onboardingListSpotlight',
    nextButton: 'onboardingListSpotlightNextButton',
    dismissButton: 'onboardingListSpotlightDoneButton',
  },
  boardSwitcherModal: {
    spotlightName: 'boardSwitcherSpotlight',
    componentName: 'boardSwitcherSpotlight',
    nextButton: 'boardSwitcherSpotlightNextButton',
    dismissButton: 'boardSwitcherSpotlightDismissButton',
  },
};

export const usePersonalProductivitySpotlightTour = ({
  source,
  hideSpotlight,
  listId,
  boardId,
  onDismissSpotlight,
}: NewUserSpotlightTourProps) => {
  const memberId = useMemberId();
  const [hasStartedTour, setHasStartedTour] = useState(false);

  const { value: spotlightGateEnabled } = useFeatureGate(
    'xf_new_user_spotlights',
  );
  const { data: board } = useBoardWorkspaceFragment({
    from: { id: boardId },
    optimistic: true,
  });

  const workspaceId = board?.idOrganization;

  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();
  const { panels, toggleInbox } = useSplitScreenSharedState();

  const { data } = useNewUserCohortFragment({
    from: { id: memberId },
  });

  const { activeSpotlight } = useSharedStateSelector(
    personalProductivityOnboardingStepSharedState,
    (state) => ({ activeSpotlight: state.step }),
  );

  const { showInboxSpotlightState } = useSharedStateSelector(
    newUserShowInboxSpotlightSharedState,
    (state) => ({ showInboxSpotlightState: state }),
  );
  const totalNewUserSpotlights = showInboxSpotlightState ? 3 : 2;

  // This will return undefined if no overrides are set
  const personalProductivityLocalOverride =
    usePersonalProductivityLocalOverride();

  const userCohort =
    personalProductivityLocalOverride ||
    data?.cohorts?.userCohortPersonalProductivity;
  const isPremiumTrialModalDismissed = workspaceId
    ? isOneTimeMessageDismissed(
        `${PREMIUM_TRIAL_MODAL_MESSAGE_DISMISSED_KEY_PREFIX}-${workspaceId}`,
      )
    : false;

  const showNewUserSpotlightTour =
    spotlightGateEnabled &&
    userCohort === 'ga' &&
    !isOneTimeMessageDismissed(NEW_USER_SPOTLIGHT_TOUR_MESSAGE_ID) &&
    isOneTimeMessageDismissed(`${NEW_USER_DEFAULT_BOARD_PREFIX}-${boardId}`) &&
    isPremiumTrialModalDismissed;

  const showExistingUserSpotlightTour =
    userCohort === 'opted_in' &&
    !hideSpotlight &&
    !isOneTimeMessageDismissed(ISLAND_NAV_SPOTLIGHT);

  const canOpen = showExistingUserSpotlightTour || showNewUserSpotlightTour;

  // Preload recent boards as the first spotlight opens
  const { idBoard: inboxBoardId } = useMemberInboxIds();
  useRecentBoards({
    skip: !(showExistingUserSpotlightTour && activeSpotlight === 0),
    excludeInboxId: inboxBoardId,
  });

  const onNextFrame = (func: () => void, delay = 0): number => {
    const nextFrameTimeoutId = window.setTimeout(() => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(func));
    }, delay);
    return nextFrameTimeoutId;
  };

  const next = useCallback(() => {
    const nextFrameTimeoutId = onNextFrame(() => {
      if (activeSpotlight === 1 && showExistingUserSpotlightTour) {
        // close the board switcher when we advance to the inbox spotlight
        boardSwitcherSharedState.setValue({
          isOpen: false,
          isSpotlightOpen: false,
        });
      }
      personalProductivityOnboardingStepSharedState.setValue({
        step: (activeSpotlight || 0) + 1,
      });
    }, 200);
    return () => window.clearTimeout(nextFrameTimeoutId);
  }, [activeSpotlight, showExistingUserSpotlightTour]);

  const dismissSpotlightMessage = useCallback(
    async (message: string) => {
      onDismissSpotlight?.();
      await dismissOneTimeMessage(message);
    },
    [dismissOneTimeMessage, onDismissSpotlight],
  );

  const dismissNewUserSpotlightTour = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'onboardingListSpotlightDoneButton',
      source: 'onboardingListSpotlight',
      attributes: {
        discoveryCampaign: 'newUserOnboarding',
      },
    });
    await dismissSpotlightMessage(NEW_USER_SPOTLIGHT_TOUR_MESSAGE_ID);
    await dismissSpotlightMessage(ISLAND_NAV_SPOTLIGHT);
  }, [dismissSpotlightMessage]);

  const startIslandNavSpotlightTour = useCallback(() => {
    personalProductivityOnboardingStepSharedState.setValue({
      step: activeSpotlight || 0,
    });
    if (
      showExistingUserSpotlightTour &&
      !hasStartedTour &&
      source === 'panelNavigation'
    ) {
      // setting state here to prevent the event from being sent multiple times
      setHasStartedTour(true);

      Analytics.sendViewedComponentEvent({
        componentType: 'spotlight',
        componentName: 'islandNavigationSpotlight',
        source: 'islandNavigationSpotlight',
      });
    }
  }, [activeSpotlight, hasStartedTour, showExistingUserSpotlightTour, source]);

  const spotlightTourNextButton = useCallback(() => {
    if (activeSpotlight === 0 && showExistingUserSpotlightTour) {
      boardSwitcherSharedState.setValue({
        isOpen: true,
        isSpotlightOpen: true, // Stops trapping focus causing focus war
      });
    }

    if (activeSpotlight === 1 && showExistingUserSpotlightTour) {
      if (!panels.inbox) {
        toggleInbox();
      }
      Analytics.sendViewedComponentEvent({
        componentType: 'spotlight',
        componentName: 'inboxSpotlight',
        source: 'inboxSpotlight',
      });
    }

    Analytics.sendClickedButtonEvent({
      buttonName: analyticsSourcesMap[source].nextButton,
      source: analyticsSourcesMap[source].spotlightName,
      attributes: {
        discoveryCampaign: showNewUserSpotlightTour
          ? 'newUserOnboarding'
          : 'existingUserIntroCampaign',
      },
    });
    next();
  }, [
    activeSpotlight,
    next,
    panels.inbox,
    showExistingUserSpotlightTour,
    showNewUserSpotlightTour,
    source,
    toggleInbox,
  ]);

  const dismissExistingUsersSpotlightTour = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'inboxSpotlightDismissButton',
      source: 'inboxSpotlight',
    });
    personalProductivityOnboardingStepSharedState.setValue({
      step: null,
      activeTarget: null,
    });

    await dismissSpotlightMessage(ISLAND_NAV_SPOTLIGHT);
    await dismissSpotlightMessage(NEW_USER_SPOTLIGHT_TOUR_MESSAGE_ID);
  }, [dismissSpotlightMessage]);

  const dismissPanelSpotlight = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'panelSpotlightDismissButton',
      source: 'panelSpotlight',
    });
    personalProductivityOnboardingStepSharedState.setValue({
      step: null,
      activeTarget: null,
    });
    if (!panels.inbox) {
      toggleInbox();
    }
    await dismissOneTimeMessage(ISLAND_NAV_SPOTLIGHT);
    await dismissOneTimeMessage(NEW_USER_SPOTLIGHT_TOUR_MESSAGE_ID);
  }, [dismissOneTimeMessage, panels.inbox, toggleInbox]);

  const dismissBoardSwitcherSpotlight = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardSwitcherSpotlightDismissButton',
      source: 'boardSwitcherSpotlight',
    });
    personalProductivityOnboardingStepSharedState.setValue({
      step: null,
      activeTarget: null,
    });
    if (!panels.inbox) {
      toggleInbox();
    }
    boardSwitcherSharedState.setValue({
      isOpen: false,
      isSpotlightOpen: false,
    });
    await dismissOneTimeMessage(ISLAND_NAV_SPOTLIGHT);
    await dismissOneTimeMessage(NEW_USER_SPOTLIGHT_TOUR_MESSAGE_ID);
  }, [dismissOneTimeMessage, panels.inbox, toggleInbox]);

  useEffect(() => {
    if (!canOpen) {
      return;
    }
    const nextFrameTimeoutId = onNextFrame(() => {
      startIslandNavSpotlightTour();
    }, 1000);
    return () => window.clearTimeout(nextFrameTimeoutId);
  }, [canOpen, startIslandNavSpotlightTour]);

  // Preloading the big gif images so that they are ready to be displayed
  // when the spotlight is shown
  useEffect(() => {
    const shouldPrefetchImages =
      userCohort === 'ga' &&
      !isOneTimeMessageDismissed(NEW_USER_SPOTLIGHT_TOUR_MESSAGE_ID) &&
      isOneTimeMessageDismissed(`${NEW_USER_DEFAULT_BOARD_PREFIX}-${boardId}`);
    const preloadImages = (paths: string[]) => {
      if (!shouldPrefetchImages) {
        return;
      }
      paths.forEach((path) => {
        const img = new Image();
        img.src = path;
      });
    };

    preloadImages([
      newUserInboxSpotlightImage,
      newUserOnboardingListSpotlightImage,
      newUserPanelSpotlightImage,
      existingUserBoardSwitcherImage,
    ]);
  }, [
    boardId,
    isOneTimeMessageDismissed,
    showNewUserSpotlightTour,
    userCohort,
  ]);

  const renderActiveSpotlight = useCallback(
    (matchesExtraLargeScreenSize?: boolean) => {
      const spotlightBGColor = token('color.background.input', '#FFFFFFF');

      const sourceToNewUserSpotlightMap: Record<
        NewUserSpotlightSource,
        number
      > = {
        panelNavigation: 0,
        inbox: 1,
        list: totalNewUserSpotlights - 1,
      };

      const existingUserSpotlights = [
        <Spotlight
          key={PANEL_NAVIGATION_SPOTLIGHT}
          heading={intl.formatMessage({
            id: 'templates.split_screen.spotlights.show-or-hide-panels',
            defaultMessage: 'Show or hide panels',
            description: 'Heading for panel navigation Spotlight',
          })}
          actionsBeforeElement={'1/3'}
          actions={[
            {
              onClick: spotlightTourNextButton,
              text: intl.formatMessage({
                id: 'templates.split_screen.spotlights.next',
                defaultMessage: 'Next',
                description: 'Next button for panel navigation Spotlight',
              }),
            },
            {
              onClick: dismissPanelSpotlight,
              text: intl.formatMessage({
                id: 'templates.split_screen.spotlights.dismiss',
                defaultMessage: 'Dismiss',
                description: 'Dismiss button for Spotlight tour',
              }),
            },
          ]}
          dialogPlacement={'top center'}
          dialogWidth={275}
          targetRadius={4}
          target={PANEL_NAVIGATION_SPOTLIGHT}
          image={newUserPanelSpotlightImage}
          targetBgColor={spotlightBGColor}
        >
          <FormattedMessage
            id="templates.split_screen.spotlights.select-each-section"
            defaultMessage="Select each section to show or hide a board, Inbox, and Planner."
            description="Panel navigation Spotlight"
          />
        </Spotlight>,
        <Spotlight
          key={BOARD_SWITCHER_SPOTLIGHT}
          heading={intl.formatMessage({
            id: 'templates.split_screen.spotlights.find-and-open-boards',
            defaultMessage: 'Find and open boards',
            description: 'Heading for board switcher Spotlight',
          })}
          actionsBeforeElement={'2/3'}
          actions={[
            {
              onClick: spotlightTourNextButton,
              text: intl.formatMessage({
                id: 'templates.split_screen.spotlights.next',
                defaultMessage: 'Next',
                description: 'Next button for board switcher Spotlight',
              }),
            },
            {
              onClick: dismissBoardSwitcherSpotlight,
              text: intl.formatMessage({
                id: 'templates.split_screen.spotlights.dismiss',
                defaultMessage: 'Dismiss',
                description: 'Dismiss button for Spotlight tour',
              }),
            },
          ]}
          dialogPlacement={
            matchesExtraLargeScreenSize ? 'right top' : 'top right'
          }
          dialogWidth={275}
          targetRadius={4}
          target={BOARD_SWITCHER_SPOTLIGHT}
          image={existingUserBoardSwitcherImage}
          targetBgColor={spotlightBGColor}
        >
          <FormattedMessage
            id="templates.split_screen.spotlights.search-by-board"
            defaultMessage="Search by board name or choose from the list."
            description="Board switcher navigation Spotlight"
          />
        </Spotlight>,
        <Spotlight
          key={INBOX_SPOTLIGHT_EXISTING_USER}
          heading={intl.formatMessage({
            id: 'templates.split_screen.spotlights.many-ways-to-capture',
            defaultMessage: 'Many ways to capture',
            description: 'Heading for Inbox Spotlight',
          })}
          actionsBeforeElement={'3/3'}
          actions={[
            {
              onClick: dismissExistingUsersSpotlightTour,
              text: intl.formatMessage({
                id: 'templates.split_screen.spotlights.done',
                defaultMessage: 'Done',
                description: 'Done button for Inbox Spotlight',
              }),
            },
          ]}
          dialogPlacement={'right top'}
          dialogWidth={275}
          targetRadius={4}
          target={INBOX_SPOTLIGHT_EXISTING_USER}
          image={newUserInboxSpotlightImage}
          targetBgColor={spotlightBGColor}
        >
          <FormattedMessage
            id="templates.split_screen.spotlights.add-cards-directly"
            defaultMessage="Add cards directly or capture from email and Slack. Select each card to find out more."
            description="Inbox Spotlight"
          />
        </Spotlight>,
      ];

      // Render the active spotlight
      const newUserPanelSpotlight = (
        <Spotlight
          heading={intl.formatMessage({
            id: 'templates.new_user_discovery.spotlight-panel-title',
            defaultMessage: 'One, two, three… panels',
            description: 'Heading for panel navigation Spotlight',
          })}
          actionsBeforeElement={`1/${totalNewUserSpotlights}`}
          dialogPlacement={'top center'}
          dialogWidth={275}
          actions={[
            {
              onClick: spotlightTourNextButton,
              text: intl.formatMessage({
                id: 'templates.new_user_discovery.spotlight-next',
                defaultMessage: 'Next',
                description: 'Next button',
              }),
            },
          ]}
          target={PANEL_NAVIGATION_SPOTLIGHT}
          targetRadius={PANEL_NAVIGATION_SPOTLIGHT_RADIUS}
          image={newUserPanelSpotlightImage}
          key={PANEL_NAVIGATION_SPOTLIGHT}
        >
          <FormattedMessage
            id="templates.new_user_discovery.spotlight-panel-description"
            defaultMessage="You can show and hide your Inbox, board, and Planner with your preferred view, and switch your boards."
            description="Panel navigation Spotlight description"
          />
        </Spotlight>
      );
      const newUserInboxSpotlight = (
        <Spotlight
          key={INBOX_SPOTLIGHT}
          heading={intl.formatMessage({
            id: 'templates.new_user_discovery.spotlight-inbox-title',
            defaultMessage: 'From email to Inbox',
            description: 'Heading for inbox Spotlight',
          })}
          actionsBeforeElement={`2/${totalNewUserSpotlights}`}
          dialogPlacement={'right top'}
          dialogWidth={275}
          actions={[
            {
              onClick: spotlightTourNextButton,
              text: intl.formatMessage({
                id: 'templates.new_user_discovery.spotlight-next',
                defaultMessage: 'Next',
                description: 'Next button',
              }),
            },
          ]}
          target={INBOX_SPOTLIGHT}
          targetRadius={INBOX_SPOTLIGHT_RADIUS}
          image={newUserInboxSpotlightImage}
        >
          <FormattedMessage
            id="templates.new_user_discovery.spotlight-inbox-description"
            defaultMessage="Add a card to your Inbox by emailing inbox@app.trello.com from the email address linked with your Trello account."
            description="Inbox Spotlight description"
          />
        </Spotlight>
      );
      const newUserListSpotlight = (
        <Spotlight
          key={ONBOARDING_LIST_SPOTLIGHT}
          heading={intl.formatMessage({
            id: 'templates.new_user_discovery.spotlight-list-title',
            defaultMessage: 'Get to know Trello',
            description: 'Heading for Getting Started list Spotlight',
          })}
          actionsBeforeElement={`${totalNewUserSpotlights}/${totalNewUserSpotlights}`}
          dialogPlacement={'right middle'}
          dialogWidth={275}
          actions={[
            {
              onClick: dismissNewUserSpotlightTour,
              text: intl.formatMessage({
                id: 'templates.new_user_discovery.spotlight-done',
                defaultMessage: 'Done',
                description: 'Done button',
              }),
            },
          ]}
          target={ONBOARDING_LIST_SPOTLIGHT}
          targetRadius={ONBOARDING_LIST_SPOTLIGHT_RADIUS}
          image={newUserOnboardingListSpotlightImage}
        >
          <FormattedMessage
            id="templates.new_user_discovery.spotlight-list-description"
            defaultMessage="Use this 'Getting Started' list to learn the basics and level up your Trello skills."
            description="Getting started lits Spotlight description"
          />
        </Spotlight>
      );
      const newUserSpotlights = showInboxSpotlightState
        ? [newUserPanelSpotlight, newUserInboxSpotlight, newUserListSpotlight]
        : [newUserPanelSpotlight, newUserListSpotlight];

      if (activeSpotlight === null) {
        personalProductivityOnboardingStepSharedState.setValue({
          activeTarget: null,
        });
        return null;
      }

      if (showExistingUserSpotlightTour) {
        const spotlightTarget = existingUserSpotlights[activeSpotlight];

        personalProductivityOnboardingStepSharedState.setValue({
          activeTarget: spotlightTarget.key,
        });
        return spotlightTarget;
      }

      if (
        !showNewUserSpotlightTour ||
        source === 'boardSwitcherModal' ||
        activeSpotlight !== sourceToNewUserSpotlightMap[source]
      ) {
        return null;
      }

      Analytics.sendViewedComponentEvent({
        componentType: 'spotlight',
        componentName: analyticsSourcesMap[source].componentName,
        source: analyticsSourcesMap[source].spotlightName,
        attributes: {
          discoveryCampaign: 'newUserOnboarding',
        },
      });

      const spotlightTarget = newUserSpotlights[activeSpotlight];
      personalProductivityOnboardingStepSharedState.setValue({
        activeTarget: spotlightTarget.key,
      });
      return spotlightTarget;
    },
    [
      activeSpotlight,
      dismissExistingUsersSpotlightTour,
      dismissNewUserSpotlightTour,
      showExistingUserSpotlightTour,
      showInboxSpotlightState,
      showNewUserSpotlightTour,
      source,
      spotlightTourNextButton,
      totalNewUserSpotlights,
      dismissPanelSpotlight,
      dismissBoardSwitcherSpotlight,
    ],
  );

  // TODO: Make this a generic function so that all location can be
  // conditionally wrapped with the spotlight manager
  const showListSpotlight = useCallback(() => {
    // Only show the spotlight if the list is the onboarding list
    // and the user has not dismissed the spotlight
    if (!listId || !showNewUserSpotlightTour) {
      return false;
    }
    return isOneTimeMessageDismissed(
      `${NEW_USER_ONBOARDING_LIST_PREFIX}-${listId}`,
    );
  }, [listId, showNewUserSpotlightTour, isOneTimeMessageDismissed]);

  return {
    renderActiveSpotlight,
    showListSpotlight,
    // Expose these for testing
    _next: next,
    _dismissNewUserSpotlightTour: dismissNewUserSpotlightTour,
    _startIslandNavSpotlightTour: startIslandNavSpotlightTour,
    _spotlightTourNextButton: spotlightTourNextButton,
    _dismissExistingUsersSpotlightTour: dismissExistingUsersSpotlightTour,
    _dismissPanelSpotlight: dismissPanelSpotlight,
    _dismissBoardSwitcherSpotlight: dismissBoardSwitcherSpotlight,
  };
};
